import { z } from "zod";
import { Id, Locale, Timestamp } from "./common.ts";
import { DiveObject, DiveObjectCreate, DiveObjectUpdate } from "./objects.ts";
import { SpotCreate, SpotDetail, SpotUpdate } from "./spots.ts";

/**
 * Diver suggestions: a diver proposes a new spot or object, or a correction to an existing
 * one. Nothing a diver submits is visible publicly until a moderator approves it; approval
 * applies the payload through the same code paths as a moderator's own write (audit row by
 * the approving moderator, with the suggestion id in the diff).
 *
 * Divers never set `status` or `slug`: approve decides publication, the server derives slugs.
 */
export const SUGGESTION_KINDS = [
  "spot_create",
  "object_create",
  "spot_edit",
  "object_edit",
] as const;
export const SuggestionKind = z.enum(SUGGESTION_KINDS);
export type SuggestionKind = z.infer<typeof SuggestionKind>;

export const SuggestionStatus = z.enum(["pending", "approved", "rejected", "withdrawn"]);
export type SuggestionStatus = z.infer<typeof SuggestionStatus>;

const Note = z.string().trim().max(1000);
const nonEmpty = (p: object) => Object.keys(p).length > 0;
const NON_EMPTY = "an edit must change at least one field";

/**
 * Per-kind payload schemas. Exported so the API validates a moderator-adjusted payload on
 * approve with exactly the diver's rules (no status, no slug, strict keys).
 *
 * Edit payloads REPLACE each given top-level key whole on approval (like PATCH): an i18n
 * object must carry every locale the diver wants to keep, `ratings` all six values. The
 * client sends complete objects; the server rejects an edit that changes nothing compared
 * to the current target with 400.
 */
export const SpotSuggestionPayload = SpotCreate.omit({
  status: true,
  slug: true,
  permanentClosure: true,
});
export const ObjectSuggestionPayload = DiveObjectCreate.omit({ status: true });
export const SpotEditPayload = SpotUpdate.omit({
  status: true,
  slug: true,
  permanentClosure: true,
}).refine(nonEmpty, NON_EMPTY);
export const ObjectEditPayload = DiveObjectUpdate.omit({ status: true }).refine(
  nonEmpty,
  NON_EMPTY,
);

export const SUGGESTION_PAYLOAD_SCHEMAS = {
  spot_create: SpotSuggestionPayload,
  object_create: ObjectSuggestionPayload,
  spot_edit: SpotEditPayload,
  object_edit: ObjectEditPayload,
} as const satisfies Record<SuggestionKind, z.ZodType>;

/**
 * POST /api/suggestions — divers only (staff write directly; a staff caller gets 400).
 * Edits require a note. `targetId` (edits) and `payload.spotId` (object_create) must name a
 * PUBLISHED, non-deleted spot/object, else 404 — a diver cannot learn that drafts exist.
 */
export const SuggestionCreate = z.discriminatedUnion("kind", [
  z
    .object({
      kind: z.literal("spot_create"),
      payload: SpotSuggestionPayload,
      note: Note.optional(),
      /** UI language the diver submitted in; the decision mail uses it (notifications.ts). */
      locale: Locale.optional(),
    })
    .strict(),
  z
    .object({
      kind: z.literal("object_create"),
      payload: ObjectSuggestionPayload,
      note: Note.optional(),
      /** UI language the diver submitted in; the decision mail uses it (notifications.ts). */
      locale: Locale.optional(),
    })
    .strict(),
  z
    .object({
      kind: z.literal("spot_edit"),
      targetId: Id,
      payload: SpotEditPayload,
      note: Note.min(1),
      /** UI language the diver submitted in; the decision mail uses it (notifications.ts). */
      locale: Locale.optional(),
    })
    .strict(),
  z
    .object({
      kind: z.literal("object_edit"),
      targetId: Id,
      payload: ObjectEditPayload,
      note: Note.min(1),
      /** UI language the diver submitted in; the decision mail uses it (notifications.ts). */
      locale: Locale.optional(),
    })
    .strict(),
]);
export type SuggestionCreate = z.input<typeof SuggestionCreate>;

/** A user as shown next to a suggestion. Deleted accounts appear as id "deleted". */
const Person = z
  .object({ id: z.string().min(1).max(64), name: z.string().min(1).max(120) })
  .strict();

const suggestionBase = {
  id: Id,
  status: SuggestionStatus,
  note: z.string().nullable(),
  submittedBy: Person,
  reviewedBy: Person.nullable(),
  /** Moderator's reason; required when rejected, optional on approval. */
  reviewReason: z.string().nullable(),
  /** The spot/object created or edited by approval; null until approved. */
  resultId: Id.nullable(),
  /** True when the moderator adjusted the payload before approving (`payload` = applied). */
  adjusted: z.boolean(),
  /** Edits: the target's `updatedAt` when the diver submitted (staleness check). */
  targetUpdatedAt: Timestamp.nullable(),
  createdAt: Timestamp,
  reviewedAt: Timestamp.nullable(),
};

type ReviewState = {
  status: SuggestionStatus;
  reviewedBy: unknown;
  reviewedAt: unknown;
  reviewReason: unknown;
  resultId: unknown;
};
const reviewInvariant = (s: ReviewState) => {
  switch (s.status) {
    case "pending":
    case "withdrawn":
      return s.reviewedBy === null && s.reviewedAt === null && s.resultId === null;
    case "approved":
      return s.reviewedBy !== null && s.reviewedAt !== null && s.resultId !== null;
    case "rejected":
      return (
        s.reviewedBy !== null &&
        s.reviewedAt !== null &&
        s.reviewReason !== null &&
        s.resultId === null
      );
  }
};

/** Stored payloads are the PARSED form (defaults filled in); after an adjusted approval, the applied one. */
export const Suggestion = z
  .discriminatedUnion("kind", [
    z
      .object({
        ...suggestionBase,
        kind: z.literal("spot_create"),
        targetId: z.null(),
        payload: SpotSuggestionPayload,
      })
      .strict(),
    z
      .object({
        ...suggestionBase,
        kind: z.literal("object_create"),
        targetId: z.null(),
        payload: ObjectSuggestionPayload,
      })
      .strict(),
    z
      .object({
        ...suggestionBase,
        kind: z.literal("spot_edit"),
        targetId: Id,
        payload: SpotEditPayload,
      })
      .strict(),
    z
      .object({
        ...suggestionBase,
        kind: z.literal("object_edit"),
        targetId: Id,
        payload: ObjectEditPayload,
      })
      .strict(),
  ])
  .refine(reviewInvariant, "review fields must match status")
  .refine(
    (s) => (s.kind.endsWith("_edit") ? s.targetUpdatedAt !== null : s.targetUpdatedAt === null),
    "targetUpdatedAt is set exactly for edits",
  );
export type Suggestion = z.infer<typeof Suggestion>;

export const SuggestionList = z.object({ suggestions: z.array(Suggestion) }).strict();
export type SuggestionList = z.infer<typeof SuggestionList>;

/**
 * GET /api/suggestions/:id. `current` is the target's present state for a diff, tagged by
 * type: null for creates, for a deleted target, and — for non-staff callers — whenever the
 * target is no longer published. `stale` = the target changed since the diver submitted
 * (`updatedAt` > `targetUpdatedAt`); approving a stale edit is 409 unless `force: true`.
 */
export const SuggestionDetail = z
  .object({
    suggestion: Suggestion,
    current: z
      .discriminatedUnion("type", [
        z.object({ type: z.literal("spot"), spot: SpotDetail }).strict(),
        z.object({ type: z.literal("object"), object: DiveObject }).strict(),
      ])
      .nullable(),
    stale: z.boolean(),
  })
  .strict()
  .refine((d) => {
    if (d.current === null) return true;
    const k = d.suggestion.kind;
    return d.current.type === "spot" ? k === "spot_edit" : k === "object_edit";
  }, "current must match the suggestion kind");
export type SuggestionDetail = z.infer<typeof SuggestionDetail>;

/**
 * GET /api/suggestions (moderators). Newest first, at most `limit` (default 50); pass the
 * last row's `createdAt` as `before` for the next page. `submittedBy` = a user id.
 * GET /api/suggestions/mine takes the same `limit`/`before` (other keys → 400).
 */
export const SuggestionListQuery = z
  .object({
    status: SuggestionStatus.optional(),
    kind: SuggestionKind.optional(),
    submittedBy: z.string().min(1).max(64).optional(),
    limit: z.coerce.number().int().min(1).max(200).default(50),
    before: Timestamp.optional(),
  })
  .strict();
export type SuggestionListQuery = z.infer<typeof SuggestionListQuery>;
export const MySuggestionsQuery = SuggestionListQuery.pick({ limit: true, before: true });

/**
 * POST /api/suggestions/:id/approve.
 * - `publish` (default true) applies to creates only (published vs draft); ignored for edits.
 * - `payload`, when given, REPLACES the diver's payload whole and must pass
 *   `SUGGESTION_PAYLOAD_SCHEMAS[kind]` (else 400; `null` is 400). The stored suggestion then
 *   carries the applied payload with `adjusted: true`.
 * - `force: true` approves a stale edit anyway (else 409 when stale).
 */
export const SuggestionApprove = z
  .object({
    publish: z.boolean().default(true),
    reason: z.string().trim().max(500).optional(),
    payload: z.unknown().optional(),
    force: z.boolean().default(false),
  })
  .strict();
export type SuggestionApprove = z.input<typeof SuggestionApprove>;

/** POST /api/suggestions/:id/reject — the reason is shown to the diver. */
export const SuggestionReject = z.object({ reason: z.string().trim().min(1).max(500) }).strict();
export type SuggestionReject = z.infer<typeof SuggestionReject>;

/** Max pending suggestions per diver; one more → 429 rate_limited. */
export const MAX_PENDING_SUGGESTIONS = 20;
